import { useState } from 'react';

import PropTypes from 'prop-types';

import Alert from '#globalComponents/Alert/Alert';
import { getErrorMessage } from '#lib/errors/errorMessages';
import { useRemoveMember, useChangeMemberRole } from '#orgFeature/models/org/mutations';

import { CREDIT_TYPES } from '../creditTypes';

import { Help } from './CreditPool';
import { InviteMember } from './InviteMember';
import MemberRow, { MEMBER_GRID } from './MemberRow';

export const MemberList = ({ members, currentUserId, orgRole, quotas, renewal, totalSeats }) => {
	const [editingLimitId, setEditingLimitId] = useState(null);
	const [confirmRemoveId, setConfirmRemoveId] = useState(null);
	const [transferDataOnRemove, setTransferDataOnRemove] = useState(true);

	const removeMutation = useRemoveMember();
	const changeRoleMutation = useChangeMemberRole();
	const isOwner = orgRole === 'owner';

	const handleRemove = (userId) => {
		removeMutation.mutate(
			{ userId, transferData: transferDataOnRemove },
			{ onSuccess: () => setConfirmRemoveId(null) },
		);
	};

	return (
		<div className="rounded-card border border-bgBase-500 bg-white">
			<div
				className={`${MEMBER_GRID} items-center border-b border-bgBase-500 px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-bgBase-700`}
			>
				<span className="whitespace-nowrap">
					Mitglied · {members.length} von {totalSeats} Plätzen
				</span>
				{CREDIT_TYPES.map((type) => (
					<span key={type.key} className="flex items-center gap-1 whitespace-nowrap">
						{type.short}
						<Help text="Links: Verbrauch diesen Monat. Rechts: Monatslimit. „Kein Limit“ heißt: darf den gemeinsamen Pool frei nutzen, bis er leer ist." />
					</span>
				))}
				<span className="text-right">Aktion</span>
			</div>

			{changeRoleMutation.isError && (
				<div className="border-b border-bgBase-500 p-4">
					<Alert tone="warning">{getErrorMessage(changeRoleMutation.error)}</Alert>
				</div>
			)}

			{members.map((member) => (
				<MemberRow
					key={member.userId}
					member={member}
					isCurrentUser={member.userId === currentUserId}
					isOwner={isOwner}
					quotas={quotas}
					renewal={renewal}
					isEditingLimit={editingLimitId === member.userId}
					isConfirmingRemove={confirmRemoveId === member.userId}
					transferDataOnRemove={transferDataOnRemove}
					onToggleLimit={(id) => setEditingLimitId((current) => (id && current !== id ? id : null))}
					onToggleRemove={(id) => setConfirmRemoveId((current) => (id && current !== id ? id : null))}
					onRemove={handleRemove}
					onRoleChange={(userId, role) => changeRoleMutation.mutate({ userId, role })}
					onTransferDataToggle={setTransferDataOnRemove}
					changeRolePending={changeRoleMutation.isPending}
					removeMutation={removeMutation}
				/>
			))}

			<InviteMember usedSeats={members.length} totalSeats={totalSeats} isOwner={isOwner} />
		</div>
	);
};

MemberList.propTypes = {
	members: PropTypes.arrayOf(
		PropTypes.shape({
			userId: PropTypes.string.isRequired,
			email: PropTypes.string,
			fullName: PropTypes.string,
			role: PropTypes.string,
			companyUsed: PropTypes.number,
			contactUsed: PropTypes.number,
			companyLimit: PropTypes.number,
			contactLimit: PropTypes.number,
		}),
	).isRequired,
	currentUserId: PropTypes.string.isRequired,
	orgRole: PropTypes.string.isRequired,
	quotas: PropTypes.shape({ company: PropTypes.number.isRequired, contact: PropTypes.number.isRequired }).isRequired,
	renewal: PropTypes.string,
	totalSeats: PropTypes.number.isRequired,
};
